// @flow

import { createSelector } from 'reselect';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectProductErrors,
  selectIsAnyCartProductWorking,
  hasValidatedAllContracts as selectHasValidatedAllContracts,
  selectHasCorporateCreateProduct,
  selectHasDomainCorporateProducts,
  selectDomainCreateOrTransferProducts,
  selectRawProductsArray,
} from '@gandi/cart';

import { selectCurrentId } from '@gandi/redux-sharing';

import { selectProductsWithDomainContactError } from 'src/Cart/DomainContacts/Wizard/modules/selectors';

import {
  selectIsAppWorking,
  selectIsPatchingDomainContacts,
} from '../../modules/app';
import { selectShopCurrent } from '../../modules/organization/selectors';

import type { FullState } from '../../types';

const selectHasProductErrors: (state: FullState) => boolean = createSelector(
  [selectProductErrors],
  errors => errors.length > 0,
);

export const selectShouldShowContactWizardAlert = (
  state: FullState,
): boolean => {
  const authenticated = selectIsAuthenticatedUser(state);
  // User is unauthenticated, pass.
  if (!authenticated) {
    return false;
  }

  const patchingDomainContacts = selectIsPatchingDomainContacts(state);
  const productsWithDomainContactErrors =
    selectProductsWithDomainContactError(state);
  return !patchingDomainContacts && productsWithDomainContactErrors.length > 0;
};

export const selectShouldShowOtherDomainErrors = (
  state: FullState,
): boolean => {
  const authenticated = selectIsAuthenticatedUser(state);
  if (!authenticated) {
    return false;
  }

  const patchingDomainContacts = selectIsPatchingDomainContacts(state);
  const productsWithDomainContactErrors =
    selectProductsWithDomainContactError(state);

  return !patchingDomainContacts && !productsWithDomainContactErrors.length;
};

export const selectIsCheckoutButtonDisabled = (state: FullState): boolean => {
  const currentId = selectCurrentId(state);

  // User is unauthenticated, pass
  if (!currentId) {
    return false;
  }

  const loading =
    selectIsAppWorking(state) || selectIsAnyCartProductWorking(state);
  const productErrors = selectHasProductErrors(state);
  const validatedContracts = selectHasValidatedAllContracts(state);

  return loading || productErrors || !validatedContracts;
};

export const selectIsMissingCorpoPack: (state: FullState) => boolean =
  createSelector(
    [
      // $FlowFixMe[incompatible-call]
      state => selectShopCurrent(state, true),
      selectHasDomainCorporateProducts,
      selectHasCorporateCreateProduct,
    ],
    (
      currentOrganization,
      cartHasCorporateProducts,
      cartHasCorporateCreateProduct,
    ) =>
      !currentOrganization?.corporate &&
      cartHasCorporateProducts &&
      !cartHasCorporateCreateProduct,
  );

export const selectNeedDomainConfiguration = (state: FullState): boolean => {
  const domains = selectDomainCreateOrTransferProducts(state);
  const hostingWithDomain = selectRawProductsArray(state).filter(
    p => p.productType === 'paas' && !!p.payload.domain,
  );

  return (
    domains.some(domain => !domain.payload?.owner) ||
    hostingWithDomain.some(hosting => !hosting.payload.domain?.owner)
  );
};
